/**
 * Tender reporting — aggregate stats over a time window + Messagerie
 * monthly recap signed by the senior-most commercial.
 *
 * 02/06/2026 — Companion to the AO subsystem. Feeds the board-meeting
 * panels (mid-year + annual) and the monthly Messagerie debrief.
 *
 * All functions are PURE — no setState, no side effects.
 */

import { getPlayerCommercialLevel } from '../../data/tenders';

const SIGNER_BY_LEVEL = {
  expert: { senderRole: 'role-sylvie-marchand', name: 'Sylvie Marchand', title: 'Directrice commerciale' },
  senior: { senderRole: 'role-marc-dupuis', name: 'Marc Dupuis', title: 'Commercial terrain' },
  junior: { senderRole: 'role-lea-bertrand', name: 'Léa Bertrand', title: 'Commerciale junior' },
};

/**
 * Aggregate tender activity over a [dayStart, dayEnd] day window from the
 * persisted `tenderHistory`. Returns null if no activity AND no pending
 * applications (so the board panels can hide gracefully).
 *
 * @param {Object} state — game state (reads tenderHistory + activeApplications)
 * @param {number} dayStart — inclusive
 * @param {number} dayEnd — inclusive
 * @returns {{ won, lost, total, winRate, securedRevenue, pending,
 *             bestClient, viaCounter, hasActivity } | null}
 */
export function buildTenderPeriodStats(state, dayStart, dayEnd) {
  const history = Array.isArray(state?.tenderHistory) ? state.tenderHistory : [];
  const apps = Array.isArray(state?.activeApplications) ? state.activeApplications : [];

  const inWindow = history.filter(h => {
    const d = h?.resolvedDay;
    return typeof d === 'number' && d >= dayStart && d <= dayEnd;
  });
  const pendingInWindow = apps.filter(a => {
    const d = a?.submittedDay;
    return typeof d === 'number' && d >= dayStart && d <= dayEnd;
  });

  if (inWindow.length === 0 && pendingInWindow.length === 0) return null;

  const won = inWindow.filter(h => h.outcome === 'won');
  const lost = inWindow.filter(h => h.outcome === 'lost');
  const viaCounter = won.filter(h => h.viaCounter).length;

  const securedRevenue = won.reduce((sum, h) => {
    const offer = h.offer || {};
    return sum + ((offer.dailyPrice || 0) * (offer.durationDays || 0));
  }, 0);

  // Best client = won tender with highest total value.
  let bestClient = null;
  let bestValue = 0;
  for (const h of won) {
    const v = ((h.offer?.dailyPrice || 0) * (h.offer?.durationDays || 0));
    if (v > bestValue) {
      bestValue = v;
      bestClient = h.clientName;
    }
  }

  const total = won.length + lost.length;
  const winRate = total === 0 ? null : Math.round((won.length / total) * 100);

  return {
    won: won.length,
    lost: lost.length,
    total,
    winRate,
    securedRevenue: Math.round(securedRevenue),
    pending: pendingInWindow.length,
    bestClient,
    bestValue: Math.round(bestValue),
    viaCounter,
    hasActivity: true,
  };
}

/**
 * Convenience — period stats for the H1 (months 1-6) of a given fiscal year.
 */
export function buildH1TenderStats(state, year) {
  const yStart = (year - 1) * 360;
  return buildTenderPeriodStats(state, yStart, yStart + 179);
}

/**
 * Convenience — period stats for a full fiscal year (360 days).
 */
export function buildYearTenderStats(state, year) {
  const yStart = (year - 1) * 360;
  return buildTenderPeriodStats(state, yStart, yStart + 359);
}

/**
 * Convenience — period stats for one in-game month (30 days). Day boundaries
 * are derived from year + month assuming 30-day months.
 */
export function buildMonthTenderStats(state, year, month) {
  const yStart = (year - 1) * 360;
  const mStart = yStart + (month - 1) * 30;
  return buildTenderPeriodStats(state, mStart, mStart + 29);
}

/**
 * Build the monthly Messagerie recap (pushed by coreSlice.nextDay on the
 * 28th of every month). Returns null if no commercial is hired OR if no
 * activity happened over the closing month.
 */
export function buildMonthlyTenderRecap(state) {
  const month = state?.month || 1;
  const year = state?.year || 1;
  const stats = buildMonthTenderStats(state, year, month);
  if (!stats) return null;
  if (!stats.hasActivity) return null;

  const level = getPlayerCommercialLevel(state.hiredSupport || {});
  if (!level) return null;
  const signer = SIGNER_BY_LEVEL[level];

  const monthNames = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const monthLabel = monthNames[month] || `mois ${month}`;

  const lines = [];
  lines.push(`Le bilan commercial de **${monthLabel}** est sorti — voici la synthèse.`);
  lines.push('');
  lines.push(`**📊 Activité AO du mois :**`);
  lines.push(`• AO soumis : ${stats.total + stats.pending}`);
  if (stats.total > 0) {
    lines.push(`• Remportés : **${stats.won}**${stats.viaCounter > 0 ? ` (dont ${stats.viaCounter} via négociation)` : ''}`);
    lines.push(`• Perdus : ${stats.lost}`);
    lines.push(`• Taux de réussite : **${stats.winRate}%**`);
  }
  if (stats.pending > 0) {
    lines.push(`• En cours d'examen : ${stats.pending}`);
  }
  if (stats.securedRevenue > 0) {
    lines.push('');
    lines.push(`💰 **CA sécurisé sur le mois : ${stats.securedRevenue.toLocaleString('fr-FR')} €**` +
      (stats.bestClient ? ` (top contrat : ${stats.bestClient})` : ''));
  }
  lines.push('');
  // Forward-looking advice based on the win rate.
  const advice = (() => {
    if (stats.total === 0 && stats.pending > 0) {
      return `Aucune décision client tombée ce mois — patience, les dossiers en cours devraient se résoudre d'ici 2 semaines.`;
    }
    if (stats.winRate === null) return `Aucune résolution ce mois.`;
    if (stats.winRate >= 70) return `Excellent ratio. On peut tenter de monter en gamme sur des AO plus ambitieux le mois prochain.`;
    if (stats.winRate >= 40) return `Ratio correct. Pour booster le taux, soit baisser légèrement les prix, soit afficher un engagement HSE plus fort.`;
    if (stats.winRate >= 20) return `Ratio faible. Mon analyse : nos prix sont probablement trop hauts par rapport au marché — revoyons ensemble la stratégie.`;
    return `Mois difficile côté AO. Concentrons-nous sur 1-2 dossiers très ciblés plutôt que de candidater partout.`;
  })();
  lines.push(`💡 ${advice}`);
  lines.push('');
  lines.push(`— ${signer.name}, ${signer.title}`);

  return {
    senderRole: signer.senderRole,
    tone: 'formal',
    subject: `Bilan commercial — ${monthLabel} (${stats.won}/${stats.total} AO)`,
    body: lines.join('\n'),
  };
}
